// ProfileCard — orchestrates the chat preview lifecycle for one
// TransferPitch profile link found in a message:
//
//   loading   → skeleton placeholder (same dimensions as the real card,
//               shimmer so the user sees activity)
//   ready     → PlayerCard or CoachCard, pixel-matched to the design
//   notfound  → tiny inline fallback that just shows the original URL
//               as a plain anchor (no scary "ERROR" toast)
//   error     → same as notfound — we never want a chat message to
//               look broken because a single card fetch failed
//
// The component is INTENTIONALLY tiny — heavy lifting lives in:
//   - useProfileCard  (fetch + Redux cache)
//   - PlayerCard / CoachCard  (presentation)
//
// All states render inside `.tp-card-chat` so the design tokens apply.

import React from "react";

import useProfileCard from "../../hooks/useProfileCard";
import PlayerCard from "./PlayerCard";
import CoachCard from "./CoachCard";

import "./ProfileCard.css";

export default function ProfileCard({ type, id, fullUrl }) {
  const { data, status } = useProfileCard(type, id);

  // While loading: a shimmer placeholder the same size as the real card
  // so the chat row doesn't visually jump when the data arrives.
  if (status === "loading" || status === "idle") {
    return (
      <div className="tp-card-chat">
        <div className="tp-card-skeleton" aria-label="Loading profile preview" />
      </div>
    );
  }

  // Backend reachable but profile not available (404, hidden, deleted)
  // OR network error. Fall back to a quiet inline link — the user can
  // still click through.
  if (status === "notfound" || status === "error" || !data) {
    return (
      <div className="tp-card-chat">
        <div className="tp-card-error">
          This profile is no longer available.
          {fullUrl && (
            <>
              {" "}
              <a href={fullUrl} target="_blank" rel="noopener noreferrer">
                {fullUrl}
              </a>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="tp-card-chat">
      {data.type === "coach" ? <CoachCard data={data} /> : <PlayerCard data={data} />}
    </div>
  );
}
